import { useDrawer } from '@billy/design-system'
import { useEffect } from 'react'
import { useLocation } from 'react-router'

import { TransactionViewTrackingContext } from '@features/transactions/enums/transactionViewTrackingContext'
import { SelectedTransaction } from '@features/transactions/types/selectedTransaction'

import { useSegment } from '../../../../../../hooks/useSegment'
import { DrawerContent } from './elements/DrawerContent'

interface TransactionDrawerProps {
  onSelectTransaction: (transaction: SelectedTransaction) => void
  transaction?: SelectedTransaction
}

const TRANSACTION_QUERY_PARAM = 'transactionId'
const OPEN_DELAY_MS = 2000

export const TransactionDrawer = ({ onSelectTransaction, transaction }: TransactionDrawerProps) => {
  const { track } = useSegment()
  const { search } = useLocation()
  const urlTransactionId = new URLSearchParams(search).get(TRANSACTION_QUERY_PARAM) ?? undefined
  const { isOpen, open } = useDrawer()

  const { id: transactionId, originatorType, transactionNo } = transaction ?? {}
  const id = transactionId ?? urlTransactionId

  /* Open drawer if the transaction ID is present in the URL */
  /* and NOT in any other case */
  useEffect(() => {
    if (isOpen || transactionId || !urlTransactionId) {
      return
    }

    const timeout = setTimeout(() => {
      open()
      track('xxx - Bookkeeping - Ledger', {
        action: 'Open drawer',
        context: TransactionViewTrackingContext.UrlParams,
        transactionId: urlTransactionId,
        transactionType: originatorType,
      })
    }, OPEN_DELAY_MS)

    return () => clearTimeout(timeout)
  }, [isOpen, transactionId, urlTransactionId, open, originatorType, track])

  /* Tracking time spent in the drawer */
  useEffect(() => {
    if (!isOpen || !id) {
      return
    }

    const openedAt = Date.now()

    /* Cleanup fires on close, on id change (switching without closing), and on unmount */
    return () => {
      const timeSpent = Date.now() - openedAt

      track('xxx - Bookkeeping - Ledger - Drawer', {
        action: 'Drawer closed',
        timeSpent: `${timeSpent}ms`,
        transactionId: id,
        transactionType: originatorType,
      })
    }
    /* originatorType is intentionally omitted, only run on isOpen or id change */
    /* id change === type change, so safe */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isOpen, track])

  if (!id) {
    return null
  }

  return <DrawerContent id={id} onSelectTransaction={onSelectTransaction} transactionNo={transactionNo ?? null} />
}
